import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import threatIntelligenceService from '../../services/threatIntelligenceService';


export const fetchLatestReportsDrawerBody = createAsyncThunk(
  'threat/fetchLatestReportsDrawerBody',
  async ({ key }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getLatestReportsDrawerBody({ signal });
      const data = res?.data ?? [];
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

export const fetchStixCoreObjectsDonutDistribution = createAsyncThunk(
  'threat/fetchStixCoreObjectsDonutDistribution',
  async ({ key, body }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getStixCoreObjectsNumber({ body, signal });
      const data = res?.data?.data?.stixCoreObjectsDistribution ?? [];
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

export const fetchStixCoreObjectsList = createAsyncThunk(
  'threat/fetchStixCoreObjectsList',
  async ({ key, body }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getStixCoreObjectsNumber({ body, signal });
      const edges = res?.data?.data?.stixCoreObjects?.edges ?? [];
      const data = edges.map((e) => e?.node).filter(Boolean);
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

export const fetchStixRelationshipsDistributionListDistribution = createAsyncThunk(
  'threat/fetchStixRelationshipsDistributionListDistributionQuery',
  async ({ key, body }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getStixCoreObjectsNumber({ body, signal });
      const data = res?.data?.data?.stixRelationshipsDistribution ?? [];
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

export const fetchStixRelationshipsPolarAreaDistribition = createAsyncThunk(
  'threat/fetchStixRelationshipsPolarAreaDistribition',
  async ({ key, body }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getStixCoreObjectsNumber({ body, signal });
      const data = res?.data?.data?.stixRelationshipsDistribution ?? [];
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

export const fetchStixRelationshipsMultiTimeSeries = createAsyncThunk(
  'threat/fetchStixRelationshipsMultiTimeSeries',
  async ({ key, body }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getStixCoreObjectsNumber({ body, signal });
      const data = res?.data?.data?.stixRelationshipsMultiTimeSeries ?? [];
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

export const fetchStixRelationshipsDistribution = createAsyncThunk(
  'threat/fetchStixRelationshipsDistribution',
  async ({ key, body }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getStixCoreObjectsNumber({ body, signal });
      const data = res?.data?.data?.stixRelationshipsDistribution ?? [];
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

export const fetchStixCoreObjectsNumber = createAsyncThunk(
  'threat/fetchStixCoreObjectsNumber',
  async ({ key, body }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getStixCoreObjectsNumber({ body, signal });
      const data = res?.data?.data?.stixCoreObjectsNumber ?? { total: 0, count: 0 };
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

/* Parameters */
export const fetchParametersByType = createAsyncThunk(
  'threat/fetchParametersByType',
  async ({ key, letter }, { rejectWithValue, signal }) => {
    try {
      const res = await threatIntelligenceService.getParametersByType({ letter, signal });
      const data = res?.data ?? [];
      return { key, data };
    } catch (err) {
      const error = err?.response?.data || err?.message || 'Request failed';
      return rejectWithValue({ key, error });
    }
  }
);

const initialItem = { data: { total: 0, count: 0 }, loading: false, error: null };
const initialArrayItem = { data: [], loading: false, error: null };

const threatIntelligenceSlice = createSlice({
  name: 'threat',
  initialState: {
    items: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ----- stixCoreObjectsNumber -----
      .addCase(fetchStixCoreObjectsNumber.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchStixCoreObjectsNumber.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = {
          data,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchStixCoreObjectsNumber.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialItem),
          loading: false,
          error: error || 'Request failed',
        };
      })

      // ----- stixRelationshipsDistribution -----
      .addCase(fetchStixRelationshipsDistribution.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchStixRelationshipsDistribution.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = { data, loading: false, error: null };
      })
      .addCase(fetchStixRelationshipsDistribution.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: false,
          error: error || 'Request failed',
        };
      })

      // ----- stixRelationshipsMultiTimeSeries -----
      .addCase(fetchStixRelationshipsMultiTimeSeries.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchStixRelationshipsMultiTimeSeries.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = { data, loading: false, error: null };
      })
      .addCase(fetchStixRelationshipsMultiTimeSeries.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: false,
          error: error || 'Request failed',
        };
      })

      // ----- StixRelationshipsPolarAreaDistribition -----
      .addCase(fetchStixRelationshipsPolarAreaDistribition.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchStixRelationshipsPolarAreaDistribition.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = { data, loading: false, error: null };
      })
      .addCase(fetchStixRelationshipsPolarAreaDistribition.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: false,
          error: error || 'Request failed',
        };
      })

      // ----- MostActiveVulnerabilities (DistributionList) -----
      .addCase(fetchStixRelationshipsDistributionListDistribution.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchStixRelationshipsDistributionListDistribution.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = { data, loading: false, error: null };
      })
      .addCase(fetchStixRelationshipsDistributionListDistribution.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: false,
          error: error || 'Request failed',
        };
      })

      // ----- LATEST REPORTS (CoreObjectsList) -----
      .addCase(fetchStixCoreObjectsList.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchStixCoreObjectsList.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = { data, loading: false, error: null };
      })
      .addCase(fetchStixCoreObjectsList.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: false,
          error: error || 'Request failed',
        };
      })

      // ----- DISTRIBITION OF DATA (CoreObjectsDonut) -----
      .addCase(fetchStixCoreObjectsDonutDistribution.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchStixCoreObjectsDonutDistribution.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = { data, loading: false, error: null };
      })
      .addCase(fetchStixCoreObjectsDonutDistribution.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: false,
          error: error || 'Request failed',
        };
      })

      /* Parameters */
      .addCase(fetchParametersByType.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchParametersByType.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = { data, loading: false, error: null };
      })
      .addCase(fetchParametersByType.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: false,
          error: error || 'Request failed',
        };
      })
      .addCase(fetchLatestReportsDrawerBody.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: true,
          error: null,
        };
      })
      .addCase(fetchLatestReportsDrawerBody.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.items[key] = { data, loading: false, error: null };
      })
      .addCase(fetchLatestReportsDrawerBody.rejected, (state, action) => {
        const { key, error } = action.payload || {};
        state.items[key] = {
          ...(state.items[key] || initialArrayItem),
          loading: false,
          error: error || 'Request failed',
        };
      });
  },
});

export default threatIntelligenceSlice.reducer;

// Selectors
export const selectThreatByKey = (state, key) =>
  state.threat.items[key] || initialItem;

export const selectThreatArrayByKey = (state, key) =>
  state.threat.items[key] || initialArrayItem;